import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, FileDown } from "lucide-react";
import { API } from "@/lib/api";
import LoadError from "@/components/LoadError";
import SignDocumentForm from "@/components/SignDocumentForm";
import OtpGate from "@/components/OtpGate";

export default function SignPublicPage() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [sess, setSess] = useState(() => sessionStorage.getItem(`mio-sign-${token}`) || "");
  const hdr = { timeout: 20000, headers: sess ? { "X-Sign-Session": sess } : {} };

  const load = async () => {
    setErr("");
    try { setDoc((await axios.get(`${API}/sign/public/${token}`, hdr)).data); }
    catch (e) { setErr(e.response?.data?.detail || (e.response ? "Link inválido." : "net")); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token, sess]);
  const verified = (s) => { sessionStorage.setItem(`mio-sign-${token}`, s); setSess(s); };

  const submit = async (data) => {
    try { setDone((await axios.post(`${API}/sign/public/${token}`, data, hdr)).data); }
    catch (e) { setErr(e.response?.data?.detail || "Não foi possível registar a assinatura."); }
  };

  const receipt = `${API}/sign/public/${token}/receipt.pdf?s=${encodeURIComponent(sess)}`;
  return (
    <div className="min-h-screen bg-slate-950 px-4 py-8">
      <div className="max-w-xl mx-auto space-y-6" data-testid="sign-public-page">
        <div className="font-display text-xl tracking-[0.22em] text-slate-200">MIO <span className="text-slate-500 text-sm tracking-normal">Sign</span></div>
        {err === "net" && <LoadError testId="sign-public-network-error" onRetry={load} />}
        {err && err !== "net" && <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-200 p-4 text-sm" data-testid="sign-public-error">{err}</div>}
        {!err && !doc && <div className="text-slate-500" data-testid="sign-public-loading">A carregar…</div>}
        {!err && doc?.otp_required && doc.status === "pending" && <OtpGate token={token} info={doc} onVerified={verified} />}
        {!err && doc?.otp_required && doc.status === "signed" && (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-center text-slate-50" data-testid="sign-public-already-signed">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400 mb-2" /> Este documento já foi assinado.
          </div>
        )}
        {!err && doc && !doc.otp_required && (done || doc.status === "signed") && (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-center space-y-3" data-testid="sign-public-done">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400" />
            <div className="text-slate-50">Documento assinado. Obrigado{doc.patient_name ? `, ${doc.patient_name}` : ""}.</div>
            <a href={receipt} target="_blank" rel="noreferrer" data-testid="sign-public-receipt"
              className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 text-emerald-200 px-4 py-2 text-sm">
              <FileDown className="w-4 h-4" /> Descarregar comprovativo
            </a>
          </div>
        )}
        {!err && doc && !doc.otp_required && !done && doc.status === "pending" && <SignDocumentForm doc={doc} onSubmit={submit} />}
      </div>
    </div>
  );
}
